import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Container,
  Paper,
  Stepper,
  Step,
  StepLabel,
} from '@mui/material';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { invitationAcceptanceSchema } from '../utils/validationSchemas';
import { hasFieldError, getFieldHelperText } from '../hooks/useFormValidation';
import { PersonAdd, CheckCircle } from '@mui/icons-material';
import ConstantComplyLogo from '../assets/logos/ConstantComplyLogo';
import { authService } from '../services/authService';
import { useAuth } from '../contexts/AuthContext';

interface InvitationFormData {
  password: string;
  password_confirm: string;
}

const AcceptInvitation: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [activeStep, setActiveStep] = useState(0);

  const {
    control,
    handleSubmit,
    formState: { errors, isSubmitting, isValid },
    watch,
  } = useForm<InvitationFormData>({
    resolver: yupResolver(invitationAcceptanceSchema),
    defaultValues: {
      password: '',
      password_confirm: '',
    },
    mode: 'onChange',
  });

  // Watch for form changes to clear errors
  const watchedValues = watch();

  const steps = ['Verify Invitation', 'Set Password', 'Complete'];

  useEffect(() => {
    if (!token) {
      setError('Invalid invitation link');
    } else {
      setActiveStep(1); // Skip to account creation if token exists
    }
  }, [token]);

  useEffect(() => {
    if (error) {
      setError('');
    }
  }, [watchedValues]);

  const onFormSubmit = async (data: InvitationFormData) => {
    setError('');
    setSuccess('');

    if (!token) {
      setError('Invalid invitation token');
      return;
    }

    setIsLoading(true);

    try {
      const result = await authService.acceptInvitation(token, data.password);
      
      if (result.success) {
        setSuccess('Account created successfully! You are now logged in.');
        setActiveStep(2);
        // Refresh user data in AuthContext
        await refreshUser();
        setTimeout(() => {
          navigate('/');
        }, 2000);
      } else {
        setError(result.error || 'Account creation failed');
      }
    } catch (err) {
      setError('An unexpected error occurred');
    } finally {
      setIsLoading(false);
    }
  };

  if (!token) {
    return (
      <Container component="main" maxWidth="sm">
        <Box sx={{ marginTop: 8, textAlign: 'center' }}>
          <Alert severity="error">
            Invalid invitation link. Please check your email for the correct link.
          </Alert>
        </Box>
      </Container>
    );
  }

  return (
    <Container component="main" maxWidth={false}>
      <Box
        sx={{
          marginTop: 4,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Box sx={{ width: { xs: '100%', sm: '80%', md: '40%', lg: '33%' }, maxWidth: 560 }}>
          <Paper
            elevation={3}
            sx={{
              padding: 4,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              width: '100%',
            }}
          >
          <Box sx={{ mb: 1, textAlign: 'center' }}>
            <ConstantComplyLogo width={200} height={110} color="#1976d2" />
            <Typography
              variant="h5"
              component="div"
              sx={{
                mt: 1,
                color: '#1976d2',
                fontWeight: 'bold',
                letterSpacing: '-0.02em',
                fontFamily: 'Arial, sans-serif',
              }}
            >
              ConstantComply
            </Typography>
          </Box>
          <Box sx={{ mb: 3 }}>
            {success ? (
              <CheckCircle sx={{ fontSize: 48, color: 'success.main' }} />
            ) : (
              <PersonAdd sx={{ fontSize: 48, color: 'primary.main' }} />
            )}
          </Box>

          <Typography component="h1" variant="h4" gutterBottom>
            Accept Invitation
          </Typography>

          <Typography variant="body1" color="text.secondary" gutterBottom textAlign="center">
            Set your password to complete your account setup and join Constant Comply.
          </Typography>

          <Box sx={{ width: '100%', mt: 3, mb: 3 }}>
            <Stepper activeStep={activeStep} alternativeLabel>
              {steps.map((label) => (
                <Step key={label}>
                  <StepLabel>{label}</StepLabel>
                </Step>
              ))}
            </Stepper>
          </Box>

          {error && (
            <Alert severity="error" sx={{ width: '100%', mb: 2 }}>
              {error}
            </Alert>
          )}

          {success && (
            <Alert severity="success" sx={{ width: '100%', mb: 2 }}>
              {success}
            </Alert>
          )}

          {!success && activeStep === 1 && (
            <Box component="form" onSubmit={handleSubmit(onFormSubmit)} sx={{ mt: 2, width: '100%' }}>
              <Controller
                name="password"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    margin="normal"
                    required
                    fullWidth
                    type="password"
                    id="password"
                    label="Password"
                    autoComplete="new-password"
                    autoFocus
                    error={hasFieldError(errors, 'password')}
                    helperText={getFieldHelperText(errors, 'password', 'Minimum 8 characters with uppercase, lowercase, and number')}
                    disabled={isLoading || isSubmitting}
                  />
                )}
              />

              <Controller
                name="password_confirm"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    margin="normal"
                    required
                    fullWidth
                    type="password"
                    id="password_confirm"
                    label="Confirm Password"
                    autoComplete="new-password"
                    error={hasFieldError(errors, 'password_confirm')}
                    helperText={getFieldHelperText(errors, 'password_confirm')}
                    disabled={isLoading || isSubmitting}
                  />
                )}
              />

              <Button
                type="submit"
                fullWidth
                variant="contained"
                sx={{ mt: 3, mb: 2 }}
                disabled={isLoading || isSubmitting || !isValid}
              >
                {isLoading || isSubmitting ? (
                  <CircularProgress size={24} />
                ) : (
                  'Create Account'
                )}
              </Button>
            </Box>
          )}

          {activeStep === 2 && (
            <Box sx={{ textAlign: 'center', mt: 3 }}>
              <Typography variant="h6" color="success.main" gutterBottom>
                Welcome to Constant Comply!
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Redirecting you to the dashboard...
              </Typography>
            </Box>
          )}

          <Box sx={{ mt: 4, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              Need help? Contact your administrator.
            </Typography>
          </Box>
        </Paper>
        </Box>
      </Box>
    </Container>
  );
};

export default AcceptInvitation;